import { useState, type JSX, type ReactNode } from 'react';
import { Outlet } from 'react-router';

import { DocsSidebar } from './docs-sidebar.js';
import { DocsTopbar } from './docs-topbar.js';

export interface DocsLayoutProps {
  children?: ReactNode;
}

function DocsContentColumn({
  children,
  toc,
}: {
  children: ReactNode;
  toc?: ReactNode;
}): JSX.Element {
  return (
    <div className="mx-auto flex w-full max-w-5xl gap-10">
      <div className="min-w-0 flex-1 docs-prose">{children}</div>
      {toc}
    </div>
  );
}

/** MUI-inspired documentation shell with top bar and left navigation. */
export function DocsLayout({ children }: DocsLayoutProps): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-docs-bg)]">
      <DocsTopbar onMenuToggle={() => setMobileOpen((open) => !open)} />
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-10 bg-black/20 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <div className="flex">
        <DocsSidebar mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
        <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
          <DocsContentColumn>{children ?? <Outlet />}</DocsContentColumn>
        </main>
      </div>
    </div>
  );
}

/** Wide layout for catalog-style pages without a right TOC column. */
export function DocsLayoutCatalog({ children }: { children: ReactNode }): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-docs-bg)]">
      <DocsTopbar onMenuToggle={() => setMobileOpen((open) => !open)} />
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-10 bg-black/20 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <div className="flex">
        <DocsSidebar mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
        <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
          <DocsContentColumn>{children}</DocsContentColumn>
        </main>
      </div>
    </div>
  );
}

/** Wide layout variant with optional right TOC column for component pages. */
export function DocsLayoutWide({
  toc,
  children,
}: {
  toc?: JSX.Element | null;
  children: ReactNode;
}): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-docs-bg)]">
      <DocsTopbar onMenuToggle={() => setMobileOpen((open) => !open)} />
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-10 bg-black/20 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <div className="flex">
        <DocsSidebar mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />
        <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
          <DocsContentColumn toc={toc}>{children}</DocsContentColumn>
        </main>
      </div>
    </div>
  );
}
